// src/components/Carousel/Carousel.jsx
import React, { useState, useRef, useEffect, useMemo } from "react";
import ExpertCard from "../ExpertCard/ExpertCard";
import { useIsMobile } from "../../hooks/useIsMobile";
import {
  makeInstantShellsFromIds,
  PRELOADED_EXPERTS,
} from "../Data/expert-pre-load-database";
import "./Carousel.css";

const Carousel = ({
  items = [],
  loading,
  error,
  hasMore,
  onNext,
  onPrev,
  onTagClick,
  enablePreloads = true, // <-- NEW: lets parent disable preloads on category clicks
  sendToIntercom = false, // <-- pass through to ExpertCard
}) => {
  const carouselRef = useRef(null);
  const [startX, setStartX] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const isMobile = useIsMobile();

  // Reset scroll position when items change (new category/search)
  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = 0;
    }
  }, [items.length, loading]);

  // ---------- helpers ----------
  const norm = (v) =>
    typeof v === "string" ? v.trim().toLowerCase() : v == null ? "" : String(v).trim().toLowerCase();

  const getId = (exp) => {
    const raw = exp?.global_id ?? exp?.globalId ?? exp?.id;
    return raw == null ? "" : String(raw);
  };
  const getSlug = (exp) => norm(exp?.slug);
  const getName = (exp) => norm(exp?.name);

  // dedupe by stable signature (id -> slug -> name|title)
  const getSignature = (exp) => {
    const id = getId(exp);
    if (id) return `id:${norm(id)}`;
    const slug = getSlug(exp);
    if (slug) return `slug:${slug}`;
    const name = getName(exp);
    const title = norm(
      exp?.featured_title_at_company ||
      exp?.headline ||
      exp?.featured_company_name ||
      ""
    );
    return `nt:${name}|${title}`;
  };

  const dedupeBySignature = (list) => {
    const seen = new Set();
    const out = [];
    for (const exp of list) {
      const sig = getSignature(exp);
      if (seen.has(sig)) continue;
      seen.add(sig);
      out.push(exp);
    }
    return out;
  };

  // ---------- preloaded ids from URL ∩ PRELOADED_EXPERTS ----------
  const { preloadedIds, preloadedVipShells, preloadedNameSet, preloadedSlugSet } = useMemo(() => {
    if (!enablePreloads) {
      // short-circuit: parent disabled preloads (e.g., on category views)
      return {
        preloadedIds: new Set(),
        preloadedVipShells: [],
        preloadedNameSet: new Set(),
        preloadedSlugSet: new Set(),
      };
    }

    try {
      const params = new URLSearchParams(window.location.search);
      const vipAll = (params.get("vip_all") || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      // only keep ids that we actually have preloaded data for
      const ids = vipAll.filter((id) => PRELOADED_EXPERTS[id]);
      const shells = makeInstantShellsFromIds(ids);

      // build fallback match sets for safety when API lacks ids
      const nameSet = new Set(shells.map(getName).filter(Boolean));
      const slugSet = new Set(shells.map(getSlug).filter(Boolean));

      return {
        preloadedIds: new Set(ids.map(String)), // fast id lookup
        preloadedVipShells: shells,
        preloadedNameSet: nameSet,
        preloadedSlugSet: slugSet,
      };
    } catch {
      return {
        preloadedIds: new Set(),
        preloadedVipShells: [],
        preloadedNameSet: new Set(),
        preloadedSlugSet: new Set(),
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enablePreloads]);

  const scrollByAmount = isMobile ? window.innerWidth * 0.8 : 800;

  const handleScrollLeft = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: -scrollByAmount, behavior: "smooth" });
    }
  };
  const handleScrollRight = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: scrollByAmount, behavior: "smooth" });
    }
  };

  const handleTouchStart = (e) => {
    setStartX(e.touches[0].clientX);
    setIsDragging(true);
  };
  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const x = e.touches[0].clientX;
    const diff = startX - x;
    if (diff > 50) {
      handleScrollRight();
      setIsDragging(false);
    } else if (diff < -50) {
      handleScrollLeft();
      setIsDragging(false);
    }
  };
  const handleTouchEnd = () => setIsDragging(false);

  // ---------- filter fetched items to remove any that are preloaded ----------
  const filteredFetchedItems = useMemo(() => {
    if (!items || items.length === 0) return [];

    const uniqueFetched = dedupeBySignature(items);

    if (!enablePreloads || preloadedVipShells.length === 0) {
      // No preloads present (or disabled): just return deduped fetched
      return uniqueFetched;
    }

    // Otherwise, remove any fetched that match a preload by id/slug/name
    return uniqueFetched.filter((exp) => {
      const id = getId(exp);
      if (id && preloadedIds.has(String(id))) return false;
      const slug = getSlug(exp);
      if (slug && preloadedSlugSet.has(slug)) return false;
      const name = getName(exp);
      if (name && preloadedNameSet.has(name)) return false;
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, preloadedIds, preloadedNameSet, preloadedSlugSet, preloadedVipShells.length, enablePreloads]);

  // ---------- final list ----------
  const finalExperts = useMemo(() => {
    if (!enablePreloads) {
      // Categories etc: behave like the original (no preloads at all)
      return items;
    }

    if (preloadedVipShells.length === 0 && items.length === 0) {
      return [];
    }
    if (items.length === 0) {
      // show preloads while waiting on search
      return preloadedVipShells;
    }
    // show preloads + fetched (filtered to avoid duplicates)
    return [...preloadedVipShells, ...filteredFetchedItems];
  }, [preloadedVipShells, filteredFetchedItems, enablePreloads, items]);

  const renderSkeletons = finalExperts.length === 0 && loading;

  return (
    <div className="expert-carousel-container">
      <div
        className="carousel-wrapper"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {!isMobile && (
          <button
            onClick={handleScrollLeft}
            className={`carousel-arrow left ${isDragging ? "disabled" : ""}`}
            aria-label="Previous"
          >
            <svg viewBox="0 0 6 12" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 1L1 6L5 11" />
            </svg>
          </button>
        )}

        <div className="carousel-track" ref={carouselRef}>
          {renderSkeletons ? (
            Array(5)
              .fill(0)
              .map((_, idx) => (
                <ExpertCard onTagClick={onTagClick} key={`loading-initial-${idx}`} loading sendToIntercom={sendToIntercom} />
              ))
          ) : (
            <>
              {finalExperts.map((expert, idx) => {
                const key =
                  getId(expert) ||
                  getSlug(expert) ||
                  getSignature(expert) ||
                  String(idx);
                return (
                  <ExpertCard
                    onTagClick={onTagClick}
                    key={key}
                    expert={expert}
                    sendToIntercom={sendToIntercom}
                  />
                );
              })}
              {loading && hasMore && finalExperts.length > 0 && (
                <ExpertCard onTagClick={onTagClick} key="loading-extra" loading sendToIntercom={sendToIntercom} />
              )}
            </>
          )}
        </div>

        {!isMobile && (
          <button
            onClick={handleScrollRight}
            className={`carousel-arrow right ${isDragging ? "disabled" : ""}`}
            aria-label="Next"
          >
            <svg viewBox="0 0 6 12" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L5 6L1 11" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default Carousel;
